import { Destroyer, EnvService, filterNullish } from '@adaptiva/core/common';
import { ErrorHandlerService } from '@adaptiva/core/error-handler';
import { LoggingService } from '@adaptiva/core/logging';
import { DynamicOverlay, OverlayService } from '@adaptiva/core/overlays';
import { EStorageType, WebStorageService } from '@adaptiva/core/storage';
import { UiService } from '@adaptiva/core/ui';
import { AuthenticationService, IServerMetadata, NativeAuthenticationRequest, OidcProvider, OidcService, SamlProvider, SamlService } from '@adaptiva/data-service';
import { ButtonComponent } from '@adaptiva/widgets/button';
import { InputComponent } from '@adaptiva/widgets/input';
import { Platform } from '@angular/cdk/platform';
import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { afterNextRender, AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, EnvironmentInjector, HostBinding, inject, OnInit, Signal, signal, viewChild, WritableSignal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { BehaviorSubject, filter, map, switchMap, take, takeUntil } from 'rxjs';
import { ForgotPasswordOverlayComponent } from './forgot-password';

@Component({
	standalone: false,
	selector: 'ada-login-form',
	templateUrl: './login-form.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class LoginFormComponent extends Destroyer implements OnInit, AfterViewInit {

	public readonly emailInput: Signal<InputComponent> = viewChild('Email');

	public readonly passwordInput: Signal<InputComponent> = viewChild('Password');

	public readonly errorMessage: WritableSignal<string> = signal('');

	public readonly isFedRamp: BehaviorSubject<boolean> = new BehaviorSubject(false);

	public readonly isSubmitting: BehaviorSubject<boolean> = new BehaviorSubject(false);

	public login: NativeAuthenticationRequest = new NativeAuthenticationRequest('', '');

	public readonly oidcProviders: BehaviorSubject<OidcProvider[]> = new BehaviorSubject([]);

	public readonly samlProviders: BehaviorSubject<SamlProvider[]> = new BehaviorSubject([]);

	public rememberEmail: boolean = false;

	public continueTo!: string;

	public callbackUri!: string;

	public readonly emailStorage: string = 'adaptiva.login.email';

	protected readonly isIdentityProvidersLoading: WritableSignal<boolean> = signal(true);

	protected readonly identityProviderError: WritableSignal<string> = signal('');

	protected readonly isActiveDirectoryConfigError: WritableSignal<boolean> = signal(false);

	protected readonly ui: UiService = inject(UiService);

	protected readonly activatedRoute: ActivatedRoute = inject(ActivatedRoute);

	protected readonly auth: AuthenticationService = inject(AuthenticationService);

	protected readonly changeDetectorRef: ChangeDetectorRef = inject(ChangeDetectorRef);

	protected readonly injector: EnvironmentInjector = inject(EnvironmentInjector);

	protected readonly logger: LoggingService = inject(LoggingService);

	protected readonly oidc: OidcService = inject(OidcService);

	protected readonly overlayService: OverlayService = inject(OverlayService);

	protected readonly router: Router = inject(Router);

	protected readonly saml: SamlService = inject(SamlService);

	protected readonly storage: WebStorageService = inject(WebStorageService);

	protected readonly platform: Platform = inject(Platform);

	protected readonly env: EnvService = inject(EnvService);

	protected readonly errorHandler: ErrorHandlerService = inject(ErrorHandlerService);

	@HostBinding('style')
	protected readonly hostStyles: Record<string, string> = { 'max-width': '30rem', 'width': '100%' };

	public ngOnInit(): void {
		this.continueTo = this.activatedRoute.snapshot.params.requestedUrl || '/';
		this.callbackUri = this.activatedRoute.snapshot.queryParams.callbackUri;

		// respond to valid authentication states triggered elsewhere
		this.auth.user.pipe(
			filterNullish(),
			// prevent infinite loop if user submits a local login request
			takeUntil(this.isSubmitting.pipe(filter(x => x))),
			this.takeUntilDestroyed()
		).subscribe({
			next: (): void => {
				this.onLoginSuccess(null);
			}
		});

		this.fetchCachedEmail();
		this.fetchProviders();
	}

	public ngAfterViewInit(): void {
		// username was prepopulated from storage -- focus password input
		afterNextRender(() => {
			if (this.login.username)
				this.passwordInput().focus();
			else
				this.emailInput().focus();
		}, {
			injector: this.injector
		});
	}

	public nativeLogin(_button: ButtonComponent): void {
		if (this.isSubmitting.value)
			return;

		if (this.rememberEmail)
			this.cacheEmail();
		else
			this.clearCachedEmail();

		this.isSubmitting.next(true);
		this.setButtonLoading(_button);
		this.auth.nativeLogin(this.login).pipe(take(1)).subscribe({
			next: () => {
				this.onLoginSuccess(_button);
			},
			error: (e: Error) => {
				this.onLoginError(e, _button);
			}
		});
	}

	public activeDirectoryLogin(_button: ButtonComponent): void {
		if (this.isSubmitting.value)
			return;

		this.isSubmitting.next(true);
		this.setButtonLoading(_button);

		this.auth.activeDirectoryLogin().pipe(take(1)).subscribe({
			next: () => {
				this.onLoginSuccess(_button);
			},
			error: (e: Error) => {
				this.onLoginError(e, _button);
				this.isActiveDirectoryConfigError.set(e instanceof HttpErrorResponse && e.status === HttpStatusCode.Unauthorized);
			}
		});
	}

	public oidcLogin(_provider: OidcProvider, _button: ButtonComponent): void {
		if (this.isSubmitting.value)
			return;

		this.isSubmitting.next(true);
		this.setButtonLoading(_button);

		this.oidc.sendAuthenticationRequest(_provider, { requestedUrl: this.continueTo }).pipe(take(1)).subscribe({
			next: () => {
				this.isSubmitting.next(false);
				this.setButtonSuccess(_button);
			},
			error: (e: Error) => {
				this.onLoginError(e, _button);
			}
		});
	}

	public samlLogin(_provider: SamlProvider, _button: ButtonComponent): void {
		if (this.isSubmitting.value)
			return;

		this.isSubmitting.next(true);
		this.setButtonLoading(_button);

		this.saml.login(_provider, this.continueTo, {
			next: () => {
				this.isSubmitting.next(false);
				this.setButtonSuccess(_button);
			},
			error: (e: Error) => {
				this.onLoginError(e, _button);
			}
		});
	}

	public forgotPassword(): void {
		this.overlayService.create(ForgotPasswordOverlayComponent, this.login.username, this.injector).pipe(
			take(1)
		).subscribe();
	}

	protected signup(): void {
		this.overlayService.load({ overlayId: 'CloudSignupOverlayComponent' }).pipe(
			switchMap((_overlay: DynamicOverlay) => _overlay.handle({
				submit: (_request: Record<'targetEmailAddress', string>): void => {
					this.overlayService.alert(
						'Successfully created account',
						`We've sent a registration link to ${_request.targetEmailAddress}.`
					);
				}
			})),
			this.takeOneUntilDestroyed()
		).subscribe({
			error: (e: Error): void => {
				this.errorHandler.defaultErrorHandler(e, 'LoginFormComponent.signup');
			}
		});
	}

	protected fetchProviders(): void {
		this.isIdentityProvidersLoading.set(true);
		this.auth.serverMetadata.pipe(
			map((_meta: IServerMetadata) => {
				// don't display disabled providers or system providers
				return [
					_meta.providers.filter(p => p.enabled && !p.systemProvider),
					_meta.samlProviders.filter(p => p.enabled && !p.systemProvider)
				];
			})
		).subscribe({
			next: ([_oidc, _saml]: [OidcProvider[], SamlProvider[]]) => {
				this.oidcProviders.next(_oidc);
				this.samlProviders.next(_saml);

				this.isIdentityProvidersLoading.set(false);
				this.identityProviderError.set('');
			},
			error: (e: Error) => {
				this.logger.error(
					'Failed to retrieve OIDC Providers',
					this.logger.extractErrorMessage(e)
				)();

				this.isIdentityProvidersLoading.set(false);
				this.identityProviderError.set('Failed to fetch OIDC Providers');
			}
		});
	}

	protected fetchCachedEmail(): void {
		const _email: string = this.storage.getRecord<string>(this.emailStorage, EStorageType.LOCAL);

		if (_email) {
			this.rememberEmail = true;
			this.login.username = _email;
		}
	}

	protected cacheEmail(): void {
		this.storage.setRecord(this.emailStorage, this.login.username, EStorageType.LOCAL);
	}

	protected clearCachedEmail(): void {
		this.storage.removeRecord(this.emailStorage, EStorageType.LOCAL);
	}

	protected setButtonLoading(_button: ButtonComponent, _isLoading: boolean = true): void {
		if (!_button)
			return;

		_button.isLoading = _isLoading;
		_button.isDisabled = _isLoading;
	}

	protected setButtonSuccess(_button: ButtonComponent): void {
		if (!_button)
			return;

		_button.isLoading = false;
		_button.icon = 'faCheck';
		_button.look = 'solid';
		_button.type = 'success';
		_button.detectChanges();
	}

	protected onLoginSuccess(_button: ButtonComponent): Promise<boolean> {
		this.setButtonSuccess(_button);
		this.errorMessage.set('');
		this.isSubmitting.next(false);

		// if callback URI is supplied, redirect entire window and exit
		if (this.callbackUri) {
			window.location.assign(this.callbackUri);

			return Promise.resolve(true);
		}

		if (this.continueTo.startsWith('/login'))
			this.continueTo = '/';

		return this.router.navigateByUrl(this.continueTo);
	}

	protected onLoginError(_error: Error, _button: ButtonComponent): void {
		let _message: string;

		if (
			_error instanceof HttpErrorResponse && (
				_error.status === HttpStatusCode.ServiceUnavailable ||
				_error.status === HttpStatusCode.GatewayTimeout
			)
		)
			_message = 'Unable to establish server connection';
		else
			_message = this.logger.extractErrorMessage(_error);

		this.logger.error('LoginFormComponent.onLoginError', _message)();
		this.errorMessage.set(_message);
		this.isSubmitting.next(false);
		this.setButtonLoading(_button, false);
		this.login.timedOneTimePassword = '';
	}

}
